import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, Loader2, ClipboardList, Rocket, Pause, Archive,
  Eye, FileUp, CheckCircle, Clock, AlertTriangle, ArrowRight,
} from 'lucide-react';
import api from '../../lib/api';

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  review_pending: { label: 'Review Pending', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  pilot_ready: { label: 'Pilot Ready', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  pilot_activated: { label: 'Pilot Activated', color: 'bg-green-100 text-green-800 border-green-300' },
  pilot_live: { label: 'Pilot Live', color: 'bg-green-200 text-green-900 border-green-400' },
  pilot_paused: { label: 'Paused', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  completed: { label: 'Completed', color: 'bg-teal-100 text-teal-700 border-teal-300' },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500 border-gray-300' },
};

const TYPE_MAP = {
  pilot: 'Pilot Rollout',
  go_live: 'Go-Live Setup',
  data_migration: 'Data Migration',
  implementation: 'Implementation',
};

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return <Badge variant="outline" className={`text-xs ${cfg.color}`}>{cfg.label}</Badge>;
}

function KpiCard({ label, value, icon: Icon, color = 'text-primary' }) {
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center bg-muted ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OnboardingDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('type', typeFilter);
      const res = await api.get(`/client-onboarding?${params}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load onboarding projects');
    }
    setLoading(false);
  }, [page, debouncedSearch, statusFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = {
    total: total,
    inProgress: data.filter(d => d.status === 'in_progress').length,
    reviewPending: data.filter(d => d.status === 'review_pending').length,
    pilotActive: data.filter(d => ['pilot_activated', 'pilot_live'].includes(d.status)).length,
  };

  return (
    <div className="space-y-6" data-testid="onboarding-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Client Onboarding</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage onboarding projects, data imports, and pilot rollouts</p>
        </div>
        <Button onClick={() => navigate('/admin/onboarding/new')} data-testid="new-onboarding-btn">
          <Plus className="h-4 w-4 mr-2" />New Onboarding
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Total Projects" value={kpis.total} icon={ClipboardList} />
        <KpiCard label="In Progress" value={kpis.inProgress} icon={Clock} color="text-blue-600" />
        <KpiCard label="Review Pending" value={kpis.reviewPending} icon={AlertTriangle} color="text-amber-600" />
        <KpiCard label="Pilot Active" value={kpis.pilotActive} icon={Rocket} color="text-green-600" />
        <KpiCard label="Blocked Imports" value={data.filter(d => d.status === 'draft').length} icon={FileUp} color="text-red-600" />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name or code..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                     className="pl-9" data-testid="onboarding-search" />
            </div>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(TYPE_MAP).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No onboarding projects found</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/admin/onboarding/new')}>
                Create First Project
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="onboarding-table">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Code</th>
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">Progress</th>
                    <th className="py-2 pr-3 font-medium">Go-Live</th>
                    <th className="py-2 pr-3 font-medium">Updated</th>
                    <th className="py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(item => (
                    <tr key={item.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`onboarding-row-${item.id}`}>
                      <td className="py-2.5 pr-3 font-mono text-xs">{item.onboardingCode}</td>
                      <td className="py-2.5 pr-3 font-medium">{item.onboardingName}</td>
                      <td className="py-2.5 pr-3 text-muted-foreground">{TYPE_MAP[item.type] || item.type}</td>
                      <td className="py-2.5 pr-3"><StatusBadge status={item.status} /></td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${item.progress?.percent || 0}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground">{item.progress?.percent || 0}%</span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-muted-foreground">
                        {item.dates?.targetGoLiveDate ? new Date(item.dates.targetGoLiveDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-muted-foreground">
                        {item.audit?.updatedAt ? new Date(item.audit.updatedAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2.5">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate(`/admin/onboarding/${item.id}`)} data-testid={`view-${item.id}`}>
                            <Eye className="h-3.5 w-3.5 mr-1" />View
                          </Button>
                          {item.status === 'draft' && (
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate(`/admin/onboarding/${item.id}`)} data-testid={`edit-${item.id}`}>
                              Edit
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > 20 && (
            <div className="flex justify-between items-center mt-4 pt-3 border-t">
              <p className="text-xs text-muted-foreground">Showing {data.length} of {total}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={data.length < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
